import _ from "lodash";
import { useFormikContext } from "formik";
import {
  Typography,
  Grid2 as Grid,
  FormControl,
  FormLabel,
  FormControlLabel,
  Radio,
  RadioGroup,
  Box,
  Checkbox,
  FormHelperText,
  IconButton,
  Select,
  InputLabel,
  useMediaQuery,
  MenuItem,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import React, { useEffect, useMemo, useState } from "react";
import { cityScraperList } from "../../data/cityScraperList";
import { KeyboardArrowUp } from "@mui/icons-material";
import { ownerInterest, workTypes } from "../../data/presets";
import MemoTextField from "../permitForms/global/components/MemoTextField";
import SpecialCreateNoc from "./SpecialCreateNoc";
import { useDispatch, useSelector } from "react-redux";
import { setNocState } from "../../store/actions/permitFormActions/nocStateActions";

const TextFields = ({ type, fieldRefs }) => {
  const formik = useFormikContext();
  const dispatch = useDispatch();
  const specialCombos = {
    duval: "jacksonville",
    "st johns": "any",
    clay: "any",
  };
  const specCounty = ["duval"];
  const { selectedRow, leftValue } = useSelector(
    (state) => state.myPermitsComponent
  );
  const { initials } = useSelector((state) => state.buildingState);
  const { createNocLoading, isSuretyRadio } = useSelector(
    (state) => state.nocState
  );

  const [optionalOpen, setOptionalOpen] = useState(false);
  const [optionalClosing, setOptionalClosing] = useState(false);
  const [workTypeOptions, setWorkTypeOptions] = useState([]);

  const selectedDoc = selectedRow?.selected_documents?.[leftValue];

  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  useEffect(() => {
    // if (selectedRow?.connect_to_city?.is_connected) {
    const workOption = workTypes[selectedRow?.county] || workTypes.others;
    setWorkTypeOptions(workOption);
    // }
  }, [selectedRow?.county]);

  const handleToggle = () => {
    if (optionalOpen) {
      // Start the "closing" animation
      setOptionalClosing(true);
      setTimeout(() => {
        setOptionalOpen(false);
        // Remove closing class once animation completes
      }, 100); // Set timeout to match animation duration
    } else {
      setOptionalOpen(true); // Open the fields
    }
  };

  useEffect(() => {
    if (!optionalOpen && optionalClosing) {
      setTimeout(() => {
        setOptionalClosing(false); // Remove closing class
      }, 300);
    }
  }, [optionalOpen, optionalClosing]);

  useEffect(() => {
    const getInitials = () => {
      const initialData = initials?.find(
        (initial) => initial?.selectedRow?.county === selectedRow?.county
      );

      if (initialData) {
        const initialValues = initialData?.values?.[0] || {};
        const inputs = selectedRow?.documents?.["noc"]?.fields || {};

        const filteredInitialValues = Object.keys(initialValues).reduce(
          (acc, key) => {
            if (initialValues[key] !== "" && initialValues[key] !== undefined) {
              acc[key] = initialValues[key];
            }
            return acc;
          },
          {}
        );

        const prefillValues = Object.keys(initialValues).reduce((acc, key) => {
          if (
            Object.prototype.hasOwnProperty.call(inputs, key) &&
            (formik?.values?.[key] === undefined ||
              formik?.values?.[key] === "")
          ) {
            acc[key] = inputs[key];
          }
          return acc;
        }, {});

        if (
          !selectedRow?.documents?.noc?.permitNumber &&
          selectedRow?.permit_number
        ) {
          prefillValues["permitNumber"] = selectedRow?.permit_number;
        }

        const newValues = {
          ...formik?.values,
          ...filteredInitialValues,
          ...prefillValues,
        };

        if (!_.isEqual(formik?.values, newValues)) {
          formik?.setValues(newValues);
        }

        // formik?.setValues({
        //   ...formik?.values,
        //   ...filteredInitialValues,
        //   ...prefillValues,
        // });
      }
    };

    if (initials) {
      getInitials();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initials,
    selectedRow?.county,
    selectedRow?.documents,
    selectedRow?.permit_number,
  ]);

  const formatMoneyValue = (value) => {
    if (value === "") {
      return "";
    }
    // Remove any non-numeric characters except the decimal point
    value = value.replace(/[^\d.]/g, "");

    // Split the input into integer and decimal parts
    let [integer, decimal] = value.split(".");

    // Limit the integer part to 10 digits
    integer = integer ? integer.slice(0, 10) : "";

    // Format the integer part with commas
    let formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    // Handle cases for decimal parts
    if (decimal === undefined) {
      // If there's no decimal part, ensure it ends with ".00"
      formattedInteger += ".00";
    } else if (decimal.length === 0) {
      // If the decimal part is empty, assume ".00"
      formattedInteger += ".00";
    } else if (decimal.length === 1) {
      // If there's only one decimal digit, add a trailing "0"
      formattedInteger += `.${decimal}0`;
    } else {
      // Otherwise, limit the decimal part to two digits
      formattedInteger += `.${decimal.slice(0, 2)}`;
    }

    // Add the dollar sign
    return `${formattedInteger}`;
  };

  const handleBlur = (event, name) => {
    if (name === "amountOfBond") {
      let { value } = event.target;
      const formattedValue = formatMoneyValue(value);
      formik?.setFieldValue(event.target.name, formattedValue);
    } else {
      formik?.handleBlur(event);
    }
  };

  const handlePermitChange = (name, value) => {
    // Remove non-alphanumeric characters and convert to uppercase
    if (value.length >= 15) {
      return;
    }
    const alphanumericOnly = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();

    let formattedPermitNumber = "";

    // Add a hyphen after the 3rd character and a period after the 9th character
    if (alphanumericOnly.length <= 3) {
      formattedPermitNumber = alphanumericOnly; // First 3 characters
    } else if (alphanumericOnly.length <= 9) {
      formattedPermitNumber =
        alphanumericOnly.substring(0, 3) + "-" + alphanumericOnly.substring(3); // e.g., B24-123456
    } else {
      formattedPermitNumber =
        alphanumericOnly.substring(0, 3) +
        "-" +
        alphanumericOnly.substring(3, 9) +
        "." +
        alphanumericOnly.substring(9); // e.g., B24-123456.000
    }

    formik?.setFieldValue(name, formattedPermitNumber); // Update formik field value
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Update the formik values using setFieldValue

    if (
      name === "permitNumber" &&
      selectedRow?.county === "Duval" &&
      selectedRow?.city?.toLowerCase() === "jacksonville"
    ) {
      handlePermitChange(name, value);
    } else if (name === "permitNumber") {
      formik?.setFieldValue(name, value.toUpperCase());
    } else {
      formik?.setFieldValue(name, value);
    }
    // No need to update dynamicValues here
  };

  const fieldProps = useMemo(() => {
    return {
      streetNumber: {
        value: formik?.values.streetNumber,
        error: formik?.touched.streetNumber && formik?.errors.streetNumber,
        helperText: formik?.touched.streetNumber && formik?.errors.streetNumber,
      },
      streetName: {
        value: formik?.values.streetName,
        error: formik?.touched.streetName && formik?.errors.streetName,
        helperText: formik?.touched.streetName && formik?.errors.streetName,
      },
      streetType: {
        value: formik?.values.streetType,
        error: formik?.touched.streetType && formik?.errors.streetType,
        helperText: formik?.touched.streetType && formik?.errors.streetType,
      },
      unitNumber: {
        value: formik?.values.unitNumber,
      },
      zip: {
        value: formik?.values.zip,
        error: formik?.touched.zip && formik?.errors.zip,
        helperText: formik?.touched.zip && formik?.errors.zip,
      },
      taxFolio: {
        value: formik?.values.taxFolio,
        error: formik?.touched.taxFolio && formik?.errors.taxFolio,
        helperText: formik?.touched.taxFolio && formik?.errors.taxFolio,
      },
      permitNumber: {
        value: formik?.values.permitNumber,
        error: formik?.touched.permitNumber && formik?.errors.permitNumber,
        helperText: formik?.touched.permitNumber && formik?.errors.permitNumber,
      },
      address: {
        value: formik?.values.address,
      },
      owner: {
        value: formik?.values.owner,
      },

      // Repeat for each important field
    };
  }, [
    formik?.errors.permitNumber,
    formik?.errors.streetName,
    formik?.errors.streetNumber,
    formik?.errors.streetType,
    formik?.errors.taxFolio,
    formik?.errors.zip,
    formik?.touched.permitNumber,
    formik?.touched.streetName,
    formik?.touched.streetNumber,
    formik?.touched.streetType,
    formik?.touched.taxFolio,
    formik?.touched.zip,
    formik?.values.address,
    formik?.values.owner,
    formik?.values.permitNumber,
    formik?.values.streetName,
    formik?.values.streetNumber,
    formik?.values.streetType,
    formik?.values.taxFolio,
    formik?.values.unitNumber,
    formik?.values.zip,
  ]);
  //? RENDER DUVAL SEPARATELY

  if (
    selectedRow?.county.toLowerCase() in specialCombos &&
    !selectedRow?.noc_created
  ) {
    return <SpecialCreateNoc />; //formik?.values={formik?.values}
  } else {
    return (
      <>
        {type === "create-noc" && initials && (
          <>
            {!selectedDoc?.created && (
              <Box sx={{ py: 1, pl: 1 }}>
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  sx={{ mb: 0, pb: 0 }}
                >
                  {/* tart by entering the address of the property being improved */}
                  Address of Property Being Improved (Mandatory)
                </Typography>
                <Typography
                  fontWeight="bold"
                  color="primary"
                  variant="body2"
                  sx={{
                    m: 0,
                    p: 0,

                    lineHeight: "10px",
                    marginTop: "0px !important",
                    marginBottom: "0px !important",
                  }}
                >
                  (Can be partial or full)
                </Typography>
              </Box>
            )}

            {!selectedDoc?.created ? (
              <>
                <MemoTextField
                  inputRef={fieldRefs.current["streetNumber"]}
                  disabled={createNocLoading}
                  fullWidth={true}
                  {...fieldProps.streetNumber}
                  label="Street Number*"
                  name="streetNumber"
                  onBlur={formik?.handleBlur}
                  onChange={handleInputChange}
                />

                {selectedRow?.county === "Duval" && (
                  <Grid container gap={1}>
                    <Grid size={{ lg: 8, md: 12 }}>
                      <MemoTextField
                        inputRef={fieldRefs.current["streetName"]}
                        disabled={createNocLoading}
                        fullWidth={true}
                        {...fieldProps.streetName}
                        label="Street Name*"
                        name="streetName"
                        onBlur={formik?.handleBlur}
                        onChange={handleInputChange}
                      />
                      {!formik?.errors.streetName && (
                        <FormHelperText
                          sx={{ fontWeight: "bold", color: "#175982", ml: 1 }}
                        >
                          Do not include street type
                        </FormHelperText>
                      )}
                    </Grid>
                    <Grid size={{ lg: 3.8, md: 12 }}>
                      <MemoTextField
                        inputRef={fieldRefs.current["streetType"]}
                        disabled={createNocLoading}
                        fullWidth={true}
                        {...fieldProps.streetType}
                        label="Street Type"
                        name="streetType"
                        onBlur={formik?.handleBlur}
                        onChange={handleInputChange}
                      />
                    </Grid>
                  </Grid>
                )}

                {selectedRow?.county !== "Duval" && (
                  <MemoTextField
                    inputRef={fieldRefs.current["streetName"]}
                    disabled={createNocLoading}
                    fullWidth={true}
                    {...fieldProps.streetName}
                    label="Street Name*"
                    name="streetName"
                    onBlur={formik?.handleBlur}
                    onChange={handleInputChange}
                  />
                )}

                <MemoTextField
                  disabled={createNocLoading}
                  fullWidth={true}
                  label="Unit Number"
                  name="unitNumber"
                  {...fieldProps.unitNumber}
                  onBlur={formik?.handleBlur}
                  onChange={handleInputChange}
                />

                <MemoTextField
                  disabled={createNocLoading}
                  fullWidth={true}
                  {...fieldProps.zip}
                  label="ZIP Code"
                  name="zip"
                  onBlur={formik?.handleBlur}
                  onChange={handleInputChange}
                />

                <Box sx={{ display: "flex" }}>
                  <Box sx={{ flex: 1 }}>
                    <MemoTextField
                      disabled={
                        createNocLoading ||
                        formik?.values?.permitCheck === true ||
                        selectedRow?.connect_to_city?.is_connected === true
                      }
                      fullWidth={true}
                      {...fieldProps.permitNumber}
                      label="Permit Number"
                      name="permitNumber"
                      onBlur={formik?.handleBlur}
                      onChange={handleInputChange}
                    />
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      alignSelf: "center",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      {cityScraperList.includes(selectedRow?.city) ? (
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="permitCheck"
                              checked={formik?.values.permitCheck}
                              onChange={formik?.handleChange}
                              onBlur={formik?.handleBlur}
                            />
                          }
                          label={
                            <Typography
                              fontWeight="bold"
                              variant="body2"
                              color="primary"
                            >
                              Generate Building Permit
                            </Typography>
                          }
                        />
                      ) : (
                        <Typography
                          px={1}
                          align="center"
                          fontWeight="bold"
                          variant="body2"
                          color="primary"
                        >
                          Automatic permit generation is not yet available for
                          this selected city
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </>
            ) : (
              <MemoTextField
                disabled
                fullWidth={true}
                {...fieldProps.address}
                label="Property Address"
                name="address"
                onChange={formik?.handleChange}
              />
            )}
            {!selectedDoc?.created && (
              <>
                <Box sx={{ pb: 1 }}></Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{ width: "75%", borderBottom: "4px solid #f3f3f3" }}
                  ></Box>
                </Box>
              </>
            )}
            <Box sx={{ py: 1, pl: 1 }}>
              {selectedDoc?.created && (
                <>
                  <Typography
                    variant={selectedDoc?.created ? "h6" : "body1"}
                    fontWeight="bold"
                    sx={{ mb: 0, pb: 0 }}
                  >
                    The following fields are available for updating:
                  </Typography>
                  <Typography
                    fontWeight="bold"
                    color="primary"
                    variant="body2"
                    sx={{
                      m: 0,
                      p: 0,
                      lineHeight: "10px",
                      marginTop: "0px !important",
                      marginBottom: "0px !important",
                    }}
                  >
                    (Click update after adding new information into NOC)
                  </Typography>
                </>
              )}
            </Box>
          </>
        )}

        {(selectedDoc?.created || type === "edit") && initials && (
          <MemoTextField
            disabled={createNocLoading}
            fullWidth={true}
            {...fieldProps.owner}
            label="Owner's Name"
            name="owner"
            onBlur={formik?.handleBlur}
            onChange={formik?.handleChange}
          />
        )}

        {selectedDoc?.created && (
          <>
            <MemoTextField
              disabled={
                createNocLoading ||
                (Boolean(selectedDoc?.created) &&
                  Boolean(selectedRow?.selectedRow?.city?.created_at)) ||
                selectedRow?.connect_to_city?.is_connected === true
              }
              fullWidth={true}
              {...fieldProps.permitNumber}
              label="Permit Number"
              name="permitNumber"
              onBlur={formik?.handleBlur}
              onChange={handleInputChange}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",

                pt: 2,
              }}
            >
              <Box
                sx={{ width: "75%", borderBottom: "4px solid #f3f3f3" }}
              ></Box>
            </Box>
          </>
        )}

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Typography
            variant="h5"
            color="primary"
            fontWeight="bold"
            sx={{ mb: 0, pb: 0 }}
          >
            Optional Fields
            <IconButton
              sx={{ ml: 1 }}
              onClick={handleToggle}
              variant="contained"
              color="primary"
            >
              {!optionalOpen ? (
                <KeyboardArrowDownIcon sx={{ fontSize: "30px" }} />
              ) : (
                <KeyboardArrowUp sx={{ fontSize: "30px" }} />
              )}
            </IconButton>
          </Typography>
        </Box>

        {(optionalOpen || optionalClosing) && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
            className={optionalOpen ? "open" : optionalClosing ? "closing" : ""}
          >
            {selectedRow?.county.toLowerCase() === "duval" && (
              <Box className="field field-1">
                <MemoTextField
                  disabled={
                    createNocLoading ||
                    formik?.values?.permitCheck === true ||
                    specCounty.includes(selectedRow?.county.toLowerCase())
                  }
                  fullWidth={true}
                  {...fieldProps.taxFolio}
                  label="Parcel ID NO."
                  name="taxFolio"
                  onBlur={formik?.handleBlur}
                  onChange={handleInputChange}
                />
              </Box>
            )}

            <Box className="field field-1">
              <FormControl fullWidth={true} variant="filled">
                <InputLabel>General Description of Improvements</InputLabel>
                <Select
                  size={mobileSize ? "small" : undefined}
                  disabled={
                    createNocLoading ||
                    (Boolean(selectedDoc?.created) &&
                      Boolean(selectedRow?.city_scraper?.created_at)) ||
                    selectedRow?.connect_to_city?.is_connected === true
                  }
                  value={formik?.values?.general}
                  onChange={(e) => {
                    const selectedWorkType = workTypeOptions.find(
                      (item) => item.label === e.target.value
                    );
                    if (selectedWorkType) {
                      formik?.setFieldValue("general", selectedWorkType.label);
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        marginTop: "1.5px",
                        maxHeight: 150, // Set the desired max height for the dropdown menu
                        width: "auto",
                      },
                    },
                  }}
                  sx={{
                    maxWidth: "100%", // Set the desired max-width for the input element
                    "& .MuiTypography-body2": {
                      whiteSpace: "wrap", // Prevent text from wrapping or shrinking
                    },
                  }}
                >
                  {workTypeOptions?.map((item, i) => (
                    <MenuItem key={i} value={item?.label}>
                      {item?.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box className="field field-2">
              <FormControl fullWidth={true} variant="filled">
                <InputLabel>
                  Owner's Interest in Site of the Improvement
                </InputLabel>
                <Select
                  size={mobileSize ? "small" : undefined}
                  disabled={createNocLoading}
                  value={formik?.values?.interest}
                  onChange={(e) => {
                    const selectedInterest = ownerInterest.find(
                      (item) => item.label === e.target.value
                    );
                    if (selectedInterest) {
                      formik?.setFieldValue("interest", selectedInterest.label);
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        marginTop: "1.5px",
                        maxHeight: 150,
                        width: "auto",
                      },
                    },
                  }}
                  sx={{
                    maxWidth: "100%",
                    "& .MuiTypography-body2": {
                      whiteSpace: "wrap",
                    },
                  }}
                >
                  {ownerInterest?.map((item, i) => (
                    <MenuItem key={i} value={item?.label}>
                      {item?.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {initials &&
              (
                initials
                  .find((initial) => initial?.county === selectedRow?.county)
                  ?.fields?.filter((field) => field.name !== "permitNumber") ||
                []
              ).map((field, i) => {
                const dynamicValue = formik?.values[field.name];
                const dynamicError =
                  formik?.touched[field.name] && formik?.errors[field.name];
                const dynamicHelperText =
                  formik?.touched[field.name] && formik?.errors[field.name];

                return (
                  <Box key={i} className={`field field-${i + 2}`}>
                    {field.name === "surety" &&
                    selectedRow?.county === "Collier" ? (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <FormControl
                          component="fieldset"
                          disabled={createNocLoading}
                          sx={{ flex: 1 }}
                        >
                          <FormLabel component="legend">Surety?</FormLabel>
                          <RadioGroup
                            row
                            name="row-radio-buttons-group"
                            value={isSuretyRadio}
                            onChange={(e) => {
                              dispatch(
                                setNocState({ isSuretyRadio: e.target.value })
                              );

                              [
                                "surety",
                                "suretyAddress",
                                "suretyPhone",
                                "amountOfBond",
                              ].forEach((field) =>
                                formik?.setFieldValue(field, "")
                              );
                            }}
                          >
                            <FormControlLabel
                              value="yes"
                              control={<Radio />}
                              label="Yes"
                            />
                            <FormControlLabel
                              value="no"
                              control={<Radio />}
                              label="No"
                            />
                          </RadioGroup>
                        </FormControl>
                      </Box>
                    ) : (
                      <MemoTextField
                        inputRef={fieldRefs.current[field.name]}
                        disabled={
                          createNocLoading ||
                          (selectedRow?.county === "Collier" &&
                            isSuretyRadio === "no" &&
                            [
                              "surety",
                              "suretyAddress",
                              "suretyPhone",
                              "amountOfBond",
                            ].includes(field.name))
                        }
                        fullWidth
                        label={field.label}
                        name={field.name}
                        onBlur={(e) => handleBlur(e, field.name)}
                        onChange={handleInputChange}
                        value={dynamicValue}
                        error={dynamicError}
                        helperText={dynamicHelperText}
                      />
                    )}
                  </Box>
                );
              })}
          </Box>
        )}

        {formik?.errors?.submit && (
          <Typography color="error" sx={{ mt: 3 }} variant="body2">
            {formik?.errors?.submit}
          </Typography>
        )}
      </>
    );
  }
};

export default React.memo(TextFields);
